// useCollectionLinkOpen — the wire from the Collection email's button to the Wall.
//
// The Email Studio's button links to `/?tab=collection`. Nothing read that
// parameter, so the reader landed on the portal chooser (CLB-077). This opens the
// Collection directly, once, after sign-in and after permissions have landed.
//
// Same shape as useNotificationDeepLink: the parameter is captured at MODULE
// LOAD, because a reader who is signed out signs in first and only then does the
// shell mount. App.js is on the LOC grandfather list (Conv #163), so this is
// consumed from AppOverlays instead.
//
// Destination, decided by the reader's own portal access (fail-closed):
//   Orders access      -> the Orders world's Collection Wall (the full one)
//   Collection access  -> the Collection-only viewer page
//   neither            -> nothing; the portal chooser stays, exactly as before

import { useEffect, useRef } from 'react';
import { useUiStore } from '../stores/uiStore';
import { usePortalPermissions, canAccessPortal } from './usePortalPermissions';
import { requestOpenCollectionWall } from '../portals/orders/shell/ordersOpenBridge';

const WANTED = (() => {
  try { return new URLSearchParams(window.location.search).get('tab') === 'collection'; } catch (_) { return false; }
})();

/** Pure: where the Collection link should land for this permissions payload. */
export function collectionLinkTarget(data) {
  if (!data) return null;
  if (canAccessPortal(data, 'orders')) return 'orders';
  if (canAccessPortal(data, 'collection')) return 'collection';
  return null;
}

export function useCollectionLinkOpen() {
  const { data, isLoading } = usePortalPermissions();
  const doneRef = useRef(false);

  useEffect(() => {
    if (!WANTED || doneRef.current || isLoading || !data) return;
    doneRef.current = true;
    const target = collectionLinkTarget(data);
    if (target) {
      const ui = useUiStore.getState();
      ui.setEntryPhase('app');
      if (target === 'orders') requestOpenCollectionWall('email');
      ui.setTab(target);
    }
    // Clear it either way, so a refresh does not re-navigate (replaceState is not routing, Conv #136).
    try { window.history.replaceState({}, '', window.location.pathname); } catch (_) { /* the address bar is cosmetic */ }
  }, [data, isLoading]);
}

export default useCollectionLinkOpen;
